import CustomViewAttributesStore from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import { ICustomViewUtil } from "csi/middleware/customView/customViewService";
import { ICustomViewQueryOptions } from "csi/middleware/customView/options/CustomViewOptions";
import { action, computed, makeObservable, observable } from "mobx";
import React from "react";
import NamesRow from "./NamesRow";

export const COLUMN_ATTRIBUTE_ID = 0;
export const COLUMN_ATTRIBUTE_NAME = 1;
export const COLUMN_CUSTOM = 2;

const headerDummyData = {
	key: "",
	name: "",
	attributeName: "",
	attributeId: "",
	value: 0,
	setValue: ()=>{},
};

export const CELL_TYPE = ["attributeId", "attributeName", "name"];

export class NamesStoreItem {
	/** key to identify attribute */
	readonly key: string;
	/** selected export name */
	@observable name: string;
	/** localized name of the attribute */
	readonly attributeName: string;
	/** id of the attribute */
	readonly attributeId: string;
	/** selected column */
	@observable value = 0;

	@action setValue = (value: number): void => {
		this.value = value;
	};

	constructor (
		key: string,
		name: string,
		attributeName: string,
		attributeId: string,
	) {
		makeObservable(this);
		this.key = key;
		this.name = name;
		this.attributeName = attributeName;
		this.attributeId = attributeId;
		this.value = (name === attributeId) ? COLUMN_ATTRIBUTE_ID : (name === attributeName) ? COLUMN_ATTRIBUTE_NAME : COLUMN_CUSTOM;
	}
}

export default class CustomViewNamesStore implements ICustomViewUtil<ICustomViewQueryOptions>{
	/** attribute selection store */
	readonly attributesStore: CustomViewAttributesStore;

	/** export names view data */
	readonly data = observable.array<NamesStoreItem>([], { deep: false });
	
	/** existing export names setting */
	private namesSettings: Record<string, string>;

	/** return item at given index */
	get = (index: number): NamesStoreItem => this.data?.[index];

	/** generate list of table rows */
	@computed get children(): React.JSX.Element[] {
		const { data } = this;
		const children = [];
		for (let i= -1 ; i <= data.length - 1; i++) {
			const item: NamesStoreItem = i < 0? headerDummyData : data[i];
			children.push(
				<NamesRow
					id={i}
					item={item}
					key={i}
					onApplyAllClick={this.onApplyAllClick}
				/>);
		};
		return children;
	}
		
	/** refresh data, preserve existing data */
	@action private refreshData = ()=> {
		const { attributesStore, data, namesSettings } = this;
		const { attributeSpecsInView } = attributesStore;
		const newData:NamesStoreItem[] = [];
		attributeSpecsInView.forEach((spec) => {
			const { typedString } = spec.key;
			const existingItem = data.find((item)=>item.key == typedString);
			if (existingItem) {
				newData.push(existingItem);
			} else {
				const attributeId = spec.key.prefix;
				const attributeName = spec.getText();
				const name = namesSettings?.[typedString] || attributeId;
				newData.push(new NamesStoreItem(typedString, name, attributeName, attributeId));
			}
		});
		this.data.replace(newData);
	};

	/** create new data, wipe existing data */
	@action private initData = ()=> {
		const { attributesStore, namesSettings } = this;
		const { attributeSpecsInView } = attributesStore;
		this.data.replace([]);
		attributeSpecsInView.forEach((spec) => {
			const { typedString } = spec.key;
			const attributeId = spec.key.prefix;
			const attributeName = spec.getText();
			const name = namesSettings?.[typedString] || attributeId;
			this.data.push(new NamesStoreItem(typedString, name, attributeName, attributeId));
		});
	};

	/** total number of items in store */
	get total(): number | undefined {
		return this.data?.length || 0;
	}

	/** change all values */
	@action onApplyAllClick = (id: number) => {
		const { data } = this;
		for (let i = 0; i < data.length; i++) {
			const item = data[i];
			// set name to "" first to avoid situation when custom name is the same as name being set (different columns with same names)
			if (item.name === item[CELL_TYPE[id]]) {
				item.name = "";
			}
			item.name = item[CELL_TYPE[id]];
			item.setValue(id);
		}
	};

	/** @override react on custom view selection */
	@action onChangeOptions = (options: ICustomViewQueryOptions): void => {
		this.namesSettings = options.namesSettings;
		this.initData();
	};

	constructor(attributesStore: CustomViewAttributesStore, namesSettings: Record<string, string>) {
		makeObservable(this);
		this.attributesStore = attributesStore;
		this.namesSettings = namesSettings;
		this.initData();
	}
}


